// LAT-789: there is deliberately no per-entity file cap. An entity's Attachments tab now
// lists every file its content references (uploads ∪ embeds ∪ citations ∪ mentions), so a
// count ceiling would reject content the entity legitimately refers to. Per-file size and
// type limits below are unchanged.
/**
 * The largest document the product accepts, raised from 50 MB to 200 MB by LAT-957 Phase 2b.
 *
 * 50 MB was a bound on what one Convex action could parse synchronously, not a statement
 * about what a person may upload: past it the single request had to hold the file, the
 * conversion and the whole parsed output inside one action's memory and its fetch timeout.
 * The large-file lane removes that coupling — a file over
 * `DOC_PROCESSING_LARGE_FILE_THRESHOLD_BYTES` is converted asynchronously by the worker in
 * page chunks and ingested from a result blob — so the ceiling is now the worker's download
 * cap. That cap is published as `maxDownloadBytes` in
 * `apps/document-processor/app/contract.json` and pinned at or above this constant by
 * `packages/convex/tests/contracts/documentProcessorTimeouts.contract.test.ts`: raising this
 * without raising the worker's cap would accept files the worker refuses to download.
 *
 * **That contract test is about the document class alone**, and deliberately so: the worker's
 * download cap bounds what its *document* routes will fetch, and only a document ever reaches
 * them. The media ceilings below are exempt because media bytes never travel that path — they
 * go straight from the browser to object storage and are never handed to the document worker.
 *
 * Client-facing copy derives from here (`formatArtifactMaxFileSize`), so nothing else has to
 * be edited to keep the messages honest.
 */
export const ARTIFACT_MAX_FILE_SIZE_BYTES = 200 * 1024 * 1024; // 200 MB documents
export const ARTIFACT_MAX_IMAGE_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB images
/**
 * A zip is never stored, only unpacked in the browser, so this ceiling bounds the archive a
 * person may *hand over* rather than anything the product keeps. It stays where images are:
 * an archive big enough to matter is a batch to re-drop, not a single upload.
 */
export const ARTIFACT_MAX_ARCHIVE_FILE_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB archives
/**
 * Audio and video are bounded by what a recording of a real meeting weighs, not by what any
 * one request can hold: their bytes go browser → object storage directly, so neither ceiling
 * is a statement about a Convex action or the document worker. Video is the looser of the two
 * because a camera writes an order of magnitude more per minute than a microphone does.
 */
export const ARTIFACT_MAX_AUDIO_FILE_SIZE_BYTES = 1024 * 1024 * 1024; // 1 GB audio
export const ARTIFACT_MAX_VIDEO_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB video
export const ARTIFACT_FILE_INPUT_ACCEPT_ATTRIBUTE = "";
/**
 * The extensions that name a recording, exported as lists because two vocabularies have to
 * agree on them: this classifier, which routes the bytes, and the Claudia attachment registry,
 * which decides what the composer offers. The Sets below are the lookup form.
 */
export const ARTIFACT_VIDEO_FILE_EXTENSIONS = [
    "avi",
    "mkv",
    "mov",
    "mp4",
    "webm",
];
export const ARTIFACT_AUDIO_FILE_EXTENSIONS = [
    "aac",
    "flac",
    "m4a",
    "mp3",
    "mpga",
    "mpeg",
    "oga",
    "ogg",
    "opus",
    "wav",
];
const VIDEO_FILE_EXTENSIONS = new Set(ARTIFACT_VIDEO_FILE_EXTENSIONS);
const AUDIO_FILE_EXTENSIONS = new Set(ARTIFACT_AUDIO_FILE_EXTENSIONS);
/**
 * Extensions that name an archive, and the MIME types that claim to be one.
 *
 * The MIME set is deliberately *weaker evidence than the extension* here, which is the
 * opposite of the media classes above: every OOXML document (`.docx`, `.xlsx`, `.pptx`) is
 * literally a zip container, and a browser, an OS or a mail gateway that sniffs bytes instead
 * of reading the name will happily hand one over as `application/zip`. Trusting that would
 * classify a spreadsheet as an archive and refuse an upload the pipeline handles perfectly.
 * See `classifyArtifactFile` for the exact precedence.
 */
const ARCHIVE_FILE_EXTENSIONS = new Set(["zip"]);
const ARCHIVE_MEDIA_TYPES = new Set(["application/zip", "application/x-zip-compressed"]);
const IMAGE_FILE_EXTENSIONS = new Set([
    "avif",
    "bmp",
    "gif",
    "heic",
    "heif",
    "jpeg",
    "jpg",
    "png",
    "svg",
    "tif",
    "tiff",
    "webp",
]);
function artifactFileExtension(name) {
    const pathParts = name.trim().toLowerCase().split(/[\\/]/);
    const basename = pathParts[pathParts.length - 1] ?? "";
    const dot = basename.lastIndexOf(".");
    return dot > 0 ? basename.slice(dot + 1) : "";
}
/**
 * Concrete `image/*` content type per image extension. Only consulted when the stored
 * MIME type cannot name one itself — an upload that arrived as `application/octet-stream`
 * or with no content type at all is still a PNG, and anything that hands the bytes to a
 * model or a browser needs a real IANA type rather than the stored placeholder.
 */
const IMAGE_EXTENSION_MEDIA_TYPES = {
    avif: "image/avif",
    bmp: "image/bmp",
    gif: "image/gif",
    heic: "image/heic",
    heif: "image/heif",
    jpeg: "image/jpeg",
    jpg: "image/jpeg",
    png: "image/png",
    svg: "image/svg+xml",
    tif: "image/tiff",
    tiff: "image/tiff",
    webp: "image/webp",
};
/**
 * The canonical "is this a document, an image, a recording, or an archive" decision.
 *
 * Every routing choice between the document-parsing pipeline and the image-description
 * pipeline must come through here (backend entry point:
 * `packages/convex/convex/artifactProcessingLib/status.ts`). A bare
 * `mimeType.startsWith("image/")` test disagrees with upload validation for a `chart.png`
 * that arrived with an absent or generic content type, and an artifact the two halves
 * classify differently gets no processing at all.
 *
 * **Precedence, and the one place it inverts.** For audio, video and images the declared MIME
 * type wins over the extension (`scan.pdf` sent as `image/png` is an image), and the extension
 * is consulted only when the MIME type says nothing useful. `"archive"` (LAT-1022) is the
 * exception, on purpose: `.docx`, `.xlsx` and `.pptx` *are* zip containers, so a client that
 * sniffs bytes rather than reading the name can report a perfectly ordinary spreadsheet as
 * `application/zip`. A zip MIME type therefore only speaks for a file whose name carries **no
 * extension at all** — any known extension is decided by the name, so a document extension can
 * never become an archive.
 */
export function classifyArtifactFile(name, mimeType) {
    const mime = mimeType?.split(";")[0]?.trim().toLowerCase() ?? "";
    const extension = artifactFileExtension(name);
    if (mime.startsWith("video/"))
        return "video";
    if (mime.startsWith("audio/"))
        return "audio";
    if (mime.startsWith("image/"))
        return "image";
    if (VIDEO_FILE_EXTENSIONS.has(extension))
        return "video";
    if (AUDIO_FILE_EXTENSIONS.has(extension))
        return "audio";
    if (IMAGE_FILE_EXTENSIONS.has(extension))
        return "image";
    if (ARCHIVE_FILE_EXTENSIONS.has(extension))
        return "archive";
    if (extension === "" && ARCHIVE_MEDIA_TYPES.has(mime))
        return "archive";
    return "document";
}
/**
 * The `image/*` content type to serve an image artifact's bytes with, or `null` when the
 * artifact is not an image at all.
 *
 * Prefers the stored MIME type and falls back to the extension, so a file `classifyArtifactFile`
 * calls an image always yields a concrete type a model or browser will accept. Returns
 * `null` for an image whose extension is unknown *and* whose MIME type says nothing —
 * callers must treat that as "cannot deliver these bytes", not as "not an image".
 */
export function resolveArtifactImageMediaType(name, mimeType) {
    if (classifyArtifactFile(name, mimeType) !== "image")
        return null;
    const mime = mimeType?.split(";")[0]?.trim().toLowerCase() ?? "";
    if (mime.startsWith("image/"))
        return mime;
    return IMAGE_EXTENSION_MEDIA_TYPES[artifactFileExtension(name)] ?? null;
}
/**
 * The size ceiling per class, and the only place any of them is chosen.
 *
 * A `Record` keyed by the class rather than a chain of tests: a new
 * `ArtifactFileClass` cannot be added without giving it a ceiling here, and no call site can
 * pick one by re-deriving the class itself.
 */
const ARTIFACT_MAX_FILE_SIZE_BYTES_BY_CLASS = {
    document: ARTIFACT_MAX_FILE_SIZE_BYTES,
    image: ARTIFACT_MAX_IMAGE_FILE_SIZE_BYTES,
    archive: ARTIFACT_MAX_ARCHIVE_FILE_SIZE_BYTES,
    audio: ARTIFACT_MAX_AUDIO_FILE_SIZE_BYTES,
    video: ARTIFACT_MAX_VIDEO_FILE_SIZE_BYTES,
};
export function getArtifactMaxFileSizeBytesForClass(fileClass) {
    return ARTIFACT_MAX_FILE_SIZE_BYTES_BY_CLASS[fileClass];
}
export function getArtifactMaxFileSizeBytes(name = "", mimeType) {
    return getArtifactMaxFileSizeBytesForClass(classifyArtifactFile(name, mimeType));
}
/** Whether a file's bytes belong in media storage rather than in Convex storage. */
export function isMediaArtifactFileClass(fileClass) {
    return fileClass === "audio" || fileClass === "video";
}
/**
 * Whether this file uploads as media — the one test the upload queue, the mutation that mints
 * the signed URL and the registration that verifies it all route on.
 */
export function isMediaArtifactFile(name, mimeType) {
    return isMediaArtifactFileClass(classifyArtifactFile(name, mimeType));
}
export function getUnsupportedArtifactFileType(name, mimeType) {
    return classifyArtifactFile(name, mimeType) === "archive" ? "archive" : null;
}
/** The one user-facing sentence for each refused class — server copy and browser copy are one. */
const UNSUPPORTED_ARTIFACT_FILE_TYPE_MESSAGES = {
    archive: "Zip files can be unpacked from the Documents screen",
};
export function describeUnsupportedArtifactFileType(type) {
    return UNSUPPORTED_ARTIFACT_FILE_TYPE_MESSAGES[type];
}
/**
 * A ceiling in the words a person reads it in — "25 MB", "200 MB", "2 GB".
 *
 * Whole units only, because every ceiling is one by construction; the GB spelling exists
 * because "2048 MB" is a number nobody checks a video against.
 */
function formatArtifactFileSizeCeiling(bytes) {
    const gigabytes = bytes / (1024 * 1024 * 1024);
    if (gigabytes >= 1 && Number.isInteger(gigabytes))
        return `${gigabytes} GB`;
    return `${Math.floor(bytes / (1024 * 1024))} MB`;
}
export function formatArtifactMaxFileSize(name = "", mimeType) {
    return formatArtifactFileSizeCeiling(getArtifactMaxFileSizeBytes(name, mimeType));
}
/**
 * The one sentence every upload surface uses to say what it takes.
 *
 * It lives here rather than beside each dropzone because there were three copies of it, all
 * reading "PDF, DOCX, PPTX, XLSX, CSV or TXT — up to 200 MB each", and the classes and the
 * ceilings both move: a copy is a sentence that will still promise the old thing after the
 * next widening. Every number in it is read from the ceilings above.
 */
export function describeSupportedArtifactUploads() {
    const documentLimit = formatArtifactFileSizeCeiling(ARTIFACT_MAX_FILE_SIZE_BYTES);
    const audioLimit = formatArtifactFileSizeCeiling(ARTIFACT_MAX_AUDIO_FILE_SIZE_BYTES);
    const videoLimit = formatArtifactFileSizeCeiling(ARTIFACT_MAX_VIDEO_FILE_SIZE_BYTES);
    return `PDF, DOCX, PPTX, XLSX, CSV, TXT, audio or video — up to ${documentLimit} per document, ${audioLimit} per audio file and ${videoLimit} per video`;
}
/**
 * The longest person-facing document name the product stores.
 *
 * No real filename comes close; the cap exists so a rename cannot push an arbitrary blob of
 * text into a name that every list row, every tool result and the search index then has to
 * carry on every read.
 */
export const ARTIFACT_DISPLAY_NAME_MAX_LENGTH = 255;
/**
 * A filename split into the part a person names and the extension that types the file.
 *
 * `extension` keeps its dot and its original casing (`""` when the name carries none), so
 * `baseName + extension` reproduces the input exactly. Renaming edits the base name only: the
 * extension belongs to the uploaded bytes, not to the label someone puts on them.
 *
 * `dot > 0` on purpose, matching the rest of this module: `.gitignore` is a dotfile, not a
 * file of type "gitignore".
 */
export function splitArtifactFileName(name) {
    const dot = name.lastIndexOf(".");
    if (dot <= 0)
        return { baseName: name, extension: "" };
    return { baseName: name.slice(0, dot), extension: name.slice(dot) };
}
